import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useJournal } from '../api/journal';
import { useActiveEvents } from '../api/events';
import { streakDay } from '../journal/StreakRow';
import { dailyHref } from '../journal/dailyDestination';
import { useOnline } from '../pwa/useOnline';
import { eventBannerId } from '../compositor/assetPaths';
import { formatEta } from '../lib/formatEta';
import { UiSticker } from '../shell/UiSticker';
import { BottomSheet, Dialog } from '../components/BottomSheet';
import { useNow } from './clocks';

// Task 7 (studio-desktop-layout): these three lived in WorldScreen.tsx. Habitat's Dashboard (the
// desk's right panel, spec D2) now mounts <TodayCard /> too, and HabitatScreen is an EAGER route
// while /world is a lazy one — importing the card from WorldScreen.tsx folded the whole World hub
// (MapPanZoom, the pin layouts, the list view) into the initial chunk: measured 156.2 → 162.0 KB
// gz. Moved here, unchanged, so both screens import the card and only the card.

/**
 * The pinned journal summary card — "Today: N of 5 done" (the "5" is `dailies.length`, never
 * hardcoded, so a future content change to the fixed daily set doesn't silently desync this copy)
 * plus a "Day N" streak chip, both linking through to `/journal` (Task 20's own screen). Reads
 * `useJournal()` independently of `WorldScreen`'s own `useWorld()` gate — same tolerance
 * `LandScreen` already gives its secondary `useCompanions()` call (that screen's own header
 * comment): a still-loading or genuinely-errored journal just omits the card rather than blocking
 * or crashing the whole World hub over a query nothing else here depends on.
 */
export function JournalSummaryCard() {
  const journal = useJournal();
  if (journal.isLoading) return <li className="today-item"><div className="skeleton journal-summary-skeleton" aria-hidden="true" /></li>;
  if (!journal.data) return null;

  const { dailies, streak } = journal.data;
  const doneCount = dailies.filter((d) => d.done).length;

  return (
    <>
      <li className="today-item"><Link to="/journal" className="today-row">
        <span className="today-row-label">{`Today: ${doneCount} of ${dailies.length} done`}</span>
        <span className="chip">{`Day ${streakDay(streak.length)}`}</span>
      </Link></li>
      {dailies.map((d) => (
        <li key={d.code} className={`today-daily${d.done ? ' is-done' : ''}`}>
          <span className="today-daily-tick" aria-hidden="true">{d.done ? '✓' : ''}</span>
          <span className="today-daily-label">{d.label}</span>
          {d.done
            ? <span className="sr-only">done</span>
            : <Link to={dailyHref(d.destination)} className="today-daily-go" aria-label={`Go: ${d.label}`}>Go</Link>}
        </li>
      ))}
    </>
  );
}

/**
 * SCREENS.md "/world — World hub additions (Phase 7)": a pinned banner row above the land grid,
 * active events only — never a future-dated one (the server's own `GET /events/active` window
 * gate, `api/events.ts`'s own header comment; this component just renders whatever it gets, no
 * client-side date filtering of its own). No active events ⇒ no ribbon at all (`null`, not an
 * empty shell) — same "absent, not an empty state" posture the journal Story section's own "no
 * tales yet" text explicitly does NOT take (that one always renders a section with a message);
 * this ribbon is closer to `ReconnectingBanner`'s own "only exists when there's something to say."
 *
 * Loading shows a shape-matched skeleton card (never a spinner, same §11.9 reasoning as every
 * other skeleton in this app). A genuine (online) fetch failure with NOTHING ever cached shows an
 * honest inline retry note; offline with nothing cached instead leaves the ribbon silently absent
 * — `WorldScreen`'s own top-level `ReconnectingBanner` already carries the one "you're offline"
 * message for this whole screen, so the ribbon doesn't duplicate it. A genuine failure once data
 * WAS cached before never reaches `isError` at all (TanStack Query keeps `status: 'success'` with
 * the last-known array once a query has ever resolved) — the ribbon simply keeps showing that
 * last-known list, satisfying SCREENS.md's own "offline: Ribbon cached or absent" row for free.
 */
export function EventRibbon() {
  const events = useActiveEvents();
  const isOnline = useOnline();
  const now = useNow(30_000);

  if (events.isLoading) {
    return (
      <li className="today-item" aria-hidden="true">
        <div className="today-row"><div className="skeleton event-ribbon-skeleton" /></div>
      </li>
    );
  }

  if (events.isError) {
    if (!isOnline) return null; // never loaded, and offline — stay absent, not a misleading "couldn't load" note
    return (
      <li className="today-item"><div className="today-row event-ribbon-error">
        <p>
          Couldn&rsquo;t load events.{' '}
          <button type="button" className="btn-quiet" onClick={() => events.refetch()}>Try again</button>
        </p>
      </div></li>
    );
  }

  const active = events.data ?? [];
  if (active.length === 0) return null;

  // <li> rows of the shared Today card — TodayCard below owns a <ul>, and WorldScreen's list view
  // wraps this and JournalSummaryCard in its own <ul className="world-list-today"> — NOT a <ul> of
  // its own here — the card's `.today-item + .today-item` divider rule needs every row to be a
  // sibling of the journal row. The <li> wrapper is not decoration: putting `role="listitem"`
  // straight on the <Link> was tried and it REPLACES the anchor's implicit `link` role, so the
  // rows stopped being announced (and findable) as links at all.
  return (
    <>
      {active.map((event) => {
        const banner = eventBannerId(event.code);
        return (
        <li key={event.code} className="today-item">
          <Link to={`/world/events/${event.code}`} className="today-row today-row-event">
            {banner && <UiSticker id={banner} className="today-row-event-art" />}
            <span className="today-row-label">{event.name}</span>
            <span className="chip">{`Ends in ${formatEta(event.endsAt, now)}`}</span>
          </Link>
        </li>
        );
      })}
    </>
  );
}

/**
 * SCREENS.md "/world — World hub (layout)" `today-card`: map-only. A HUD pill in the
 * left cluster (with Map/List) opens a centered overlay of the event + journal rows.
 * The overlay pops in/out (`--t-enter` / `--t-leave`) unless `prefers-reduced-motion`.
 * List view does not use this — it keeps the rows in document flow. Starts closed
 * every visit (a modal, not a session-remembered peek).
 */
export function TodayCard() {
  const isOnline = useOnline();
  const journal = useJournal();
  const events = useActiveEvents();
  const [open, setOpen] = useState(false);
  const fabRef = useRef<HTMLButtonElement>(null);

  const active = events.data ?? [];
  const hasEvents = active.length > 0;
  const eventsError = events.isError && isOnline;
  const undone = journal.data?.dailies.filter((d) => !d.done).length ?? 0;
  const hasJournal = journal.data != null;
  const notify = undone > 0 || hasEvents;

  if (journal.isLoading) {
    return <div className="today-fab today-fab-skeleton skeleton" aria-hidden="true" />;
  }

  if (!hasJournal && !hasEvents && !eventsError && !events.isLoading) {
    return null;
  }

  return (
    <>
      <button
        ref={fabRef}
        type="button"
        className="today-fab"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <svg className="today-fab-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span>Today</span>
        {notify && <span className="today-fab-dot" />}
      </button>
      <BottomSheet
        variant="overlay"
        open={open}
        onOpenChange={setOpen}
        restoreFocusRef={fabRef}
        contentClassName="today-overlay"
        overlayClassName="today-overlay-backdrop"
      >
        <div className="sheet-header">
          <Dialog.Title asChild>
            <h2>Today</h2>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button type="button" className="sheet-close" aria-label="Close today">Close</button>
          </Dialog.Close>
        </div>
        <Dialog.Description className="sr-only">
          Daily tasks and live events.
        </Dialog.Description>
        <ul className="today-card-body" aria-label="Today">
          <EventRibbon />
          <JournalSummaryCard />
        </ul>
      </BottomSheet>
    </>
  );
}
